import { usePortfolioTutorialsState } from "@/tools/aggregate/portfolio-export/PortfolioTutorialContext";
import { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { useLssTutorialState } from "@/tools/risk-bands/large-site-screen/context/LssTutorialContext";
import { useEffect, useRef } from "react";
const demoLargeSiteSchema: PortfolioExportJobSchema = {
  id: "tutorial-lss-schema-id",
  name: "Tutorial Demo Site",
  assets: {
    errorCount: 0,
    totalCount: 1,
    processedCount: 0,
    unprocessedCount: 1
  },
  startTime: Date.now(),
  status: "processing",
  summaryId: "",
  userId: "",
  email: "",
  rerunEmail: null,
  rerunAt: null,
  groupId: "",
  groupName: "",
  secret: undefined as any,
  isLargeSite: true,
  isPrivate: true,
  isSummaryGenerated: false,
  stamps: [],
  incorporatedHazards: [],
  metadata: undefined,
  regionType: undefined,
  autoAdaptationStatus: null,
  collectionVersion: null,
  updateAvailable: false,
  adaptations: undefined
};

/**
 * Adapts the LSA tour's mocked-row flags (from LssTutorialContext) into Portfolio's own
 * tutorial-driven fake table row (PortfolioTutorialContext), reusing the existing injection
 * in PortfolioExportJobTableBody rather than duplicating it for Large Site Analysis.
 *
 * This component is rendered inside PortfolioExportTool's tree, so it's the only place on the
 * Large Site Analysis page that can actually reach the real PortfolioTutorialProvider — the tour
 * itself (LargeSiteScreenTutorial) is a sibling of PortfolioExportTool, not a descendant, so it
 * can't call usePortfolioTutorialsState() directly. This bridge republishes the refs/setters the
 * tour needs (for the download menu, actions menu, edit/delete modals, etc.) via LssTutorialContext's
 * portfolioBridge, which both components DO share.
 */
export const LargeSiteScreenTutorialBridge = () => {
  const {
    showTutorialProcessingRow,
    showTutorialCompletedRow,
    setPortfolioBridge
  } = useLssTutorialState();
  const {
    setTutorialNewRunState,
    setTutorialPortfolioSchema,
    analysisRun,
    portfolioItem,
    summaryQueueModal,
    outputTypeModal,
    editModal,
    deleteModal,
    setShowSummaryModal,
    handleSummaryStep,
    handleSummaryGenerated,
    setShowDownloadDropdown,
    setSelectedDownloadItemIndex,
    setShowPointLevelResultsModal,
    setShowMoreDropdown,
    setShowTutorialEditModal,
    setShowTutorialDeleteModal
  } = usePortfolioTutorialsState();
  useEffect(() => {
    if (showTutorialProcessingRow) {
      setTutorialNewRunState("UPLOADING");
      setTutorialPortfolioSchema({
        ...demoLargeSiteSchema,
        status: "processing"
      });
    } else if (showTutorialCompletedRow) {
      setTutorialNewRunState("INITIAL");
      setTutorialPortfolioSchema({
        ...demoLargeSiteSchema,
        status: "completed"
      });
    } else {
      setTutorialNewRunState("INITIAL");
    }
  }, [showTutorialProcessingRow, showTutorialCompletedRow, setTutorialNewRunState, setTutorialPortfolioSchema]);

  // `handleSummaryStep`/`handleSummaryGenerated` are recreated on every render upstream (they're
  // plain functions, not memoized). Hold them in refs so they don't force the publish effect below
  // to re-run — otherwise the effect (which calls setState) would run every render and loop
  // infinitely ("Maximum update depth exceeded"), which froze all rendering and blocked navigation.
  const handleSummaryStepRef = useRef(handleSummaryStep);
  handleSummaryStepRef.current = handleSummaryStep;
  const handleSummaryGeneratedRef = useRef(handleSummaryGenerated);
  handleSummaryGeneratedRef.current = handleSummaryGenerated;

  // Publish the bridge only when a real dependency changes. The refs/modals arrive via a one-time
  // debounce (so this runs a handful of times, then settles); the setters are stable. Republishing
  // unconditionally every render is what caused the infinite loop.
  useEffect(() => {
    setPortfolioBridge({
      analysisRun,
      portfolioItem,
      summaryQueueModal,
      outputTypeModal,
      editModal,
      deleteModal,
      setShowSummaryModal,
      handleSummaryStep: () => handleSummaryStepRef.current(),
      handleSummaryGenerated: () => handleSummaryGeneratedRef.current(),
      setShowDownloadDropdown,
      setSelectedDownloadItemIndex,
      setShowPointLevelResultsModal,
      setShowMoreDropdown,
      setShowTutorialEditModal,
      setShowTutorialDeleteModal
    });
  }, [analysisRun, portfolioItem, summaryQueueModal, outputTypeModal, editModal, deleteModal, setShowSummaryModal, setShowDownloadDropdown, setSelectedDownloadItemIndex, setShowPointLevelResultsModal, setShowMoreDropdown, setShowTutorialEditModal, setShowTutorialDeleteModal, setPortfolioBridge]);
  return null;
};